import { Buffer } from "buffer";
import { handleKMZ, Mission } from "@rogoag/shared-ts";
import type { Job } from "@domain/CRM/job";
import type { IAttachmentDownloader } from "@ports/browser/IAttachmentDownloader";
import type { IMissionLoader } from "@ports/missions/IMissionLoader";

export interface MissionsLoaderDeps {
	downloader: IAttachmentDownloader;
}

/**
 * Stateful adapter that implements `IMissionLoader` by downloading attachment
 * bytes through the injected `IAttachmentDownloader` and parsing them with
 * `Mission.load`. Per-instance `WeakMap` caches mean repeated calls for the
 * same `Job` reference within a session skip the network.
 *
 * Lives in 4-Infrastructure because the cache is real instance state and the
 * adapter is the bridge between an IO port (downloader) and the domain
 * `Mission` parser. The Application layer talks to the `IMissionLoader` port,
 * not this class.
 */
export class MissionsLoader implements IMissionLoader {
	private readonly exeCache = new WeakMap<Job, Mission>();
	private readonly intCache = new WeakMap<Job, Mission>();
	private readonly ptsShpCache = new WeakMap<Job, Mission>();

	constructor(private readonly deps: MissionsLoaderDeps) {}

	async loadExe(job: Job): Promise<Mission | undefined> {
		const cached = this.exeCache.get(job);
		if (cached) return cached;
		const mission = await this.loadKmlMission(job, "exeMission", "EXE");
		if (mission) this.exeCache.set(job, mission);
		return mission;
	}

	async loadInt(job: Job): Promise<Mission | undefined> {
		const cached = this.intCache.get(job);
		if (cached) return cached;
		const mission = await this.loadKmlMission(job, "intMission", "INT");
		if (mission) this.intCache.set(job, mission);
		return mission;
	}

	async loadPtsShp(
		job: Job,
		expectedType?: string,
	): Promise<Mission | "no-attachment" | "download-failed" | "parse-failed"> {
		const cached = this.ptsShpCache.get(job);
		if (cached) return cached;

		const ptsAttachments = job.attachments.pointsSHP ?? [];
		const zonesAttachments = job.attachments.zonesSHP ?? [];
		const attachments = [...ptsAttachments, ...zonesAttachments];
		if (attachments.length === 0) return "no-attachment";

		const files = await Promise.all(
			attachments.map(async (att) => {
				if (!att.url) return null;
				try {
					const data = await this.deps.downloader.load<ArrayBuffer>(att, job.id, "arraybuffer");
					if (!data) return null;
					// Wrap the ArrayBuffer in a Buffer (the polyfill from `buffer`) before handing it
					// off to MissionLoader → shpjs. shpjs's `isArrayBuffer` check uses `subject
					// instanceof global.ArrayBuffer`, but Vite's `define: { global: {} }` rewrites
					// `global` to an empty object at build time, so that check throws TypeError. By
					// passing a Buffer, shpjs's `Buffer.isBuffer(b)` short-circuits first and the
					// broken instanceof check is never reached.
					return { filename: att.filename, data: Buffer.from(data) };
				} catch (err) {
					console.error(`Pts SHP attachment download failed (${att.filename}):`, err);
					return null;
				}
			}),
		);
		const valid = files.filter((f) => f !== null) as Array<{ filename: string; data: Buffer }>;
		if (valid.length === 0) {
			console.error(
				`Pts SHP load: every attachment download returned empty for job ${job.id}`,
				attachments.map((a) => a.filename),
			);
			return "download-failed";
		}

		try {
			// Cast through unknown because Mission.load's FileData declares `data: string | ArrayBuffer`,
			// but Buffer is required at runtime to dodge the shpjs `instanceof global.ArrayBuffer` bug
			// described above. Buffer extends Uint8Array which exposes a backing ArrayBuffer, so
			// downstream consumers that genuinely need bytes still work.
			//
			// `selectedIdColumn` mirrors exit-interview-lambda's `selectedIdColumn: this.job
			// .selectedSampleColumn`. Without it the loader only recognizes a hardcoded set of
			// id column names; SHPs that key on e.g. "sp_number" get no parsed sample ids and
			// produce a Mission with zero centroids.
			const mission = await Mission.load(valid as unknown as { filename: string; data: ArrayBuffer }[], {
				expectedType,
				fieldName: job.fieldName,
				selectedIdColumn: job.selectedSampleColumn,
			});
			this.ptsShpCache.set(job, mission);
			return mission;
		} catch (err) {
			console.error(
				`Pts SHP parse failed for job ${job.id}`,
				{ filenames: valid.map((f) => f.filename), expectedType },
				err,
			);
			return "parse-failed";
		}
	}

	private async loadKmlMission(
		job: Job,
		attachmentKey: "exeMission" | "intMission",
		intOrExe: "INT" | "EXE",
	): Promise<Mission | undefined> {
		const attachments = job.attachments[attachmentKey];
		if (!attachments || attachments.length === 0) return undefined;
		const attachment = attachments[0];
		if (!attachment.url) return undefined;

		const isKmz = /\.kmz$/i.test(attachment.filename);
		let kml: string | undefined;

		if (isKmz) {
			const kmz = await this.deps.downloader.load<Buffer>(attachment, job.id, "arraybuffer");
			if (!kmz) return undefined;
			kml = await handleKMZ(kmz, attachment.filename);
		} else {
			kml = await this.deps.downloader.load<string>(attachment, job.id, "text");
		}

		if (!kml) return undefined;

		const filename = isKmz ? attachment.filename.replace(/\.kmz$/i, ".kml") : attachment.filename;
		try {
			return await Mission.load({ data: kml, filename }, {}, intOrExe);
		} catch (err) {
			console.warn(`Failed to parse ${intOrExe} mission:`, err);
			return undefined;
		}
	}
}
